import type { Profile } from '../core/types';
import type { State, Store } from '../state/store';
import {
  CLASS_COLOURS, classMean, classSpread, separability, type TrainingClass,
} from '../render/sam';
import { assess } from '../render/accuracy';
import { samBands } from '../render/style';

const rgb = (c: [number, number, number]) => `rgb(${c[0]},${c[1]},${c[2]})`;

/**
 * Training classes for the spectral-angle classifier. Clicking the map adds a point to
 * whichever class is active, so the workflow is: name a class, click examples of it,
 * move to the next.
 */
export function ClassPanel(
  { store, state, profile }: { store: Store; state: State; profile: Profile },
) {
  const { classes, activeClass } = state;
  const set = (next: TrainingClass[]) => store.set({ classes: next });
  const pairs = separability(classes);
  const worst = pairs[0];

  const addClass = () => {
    const n = classes.length;
    set([...classes, {
      id: `c${Date.now()}`, name: `Class ${n + 1}`,
      colour: CLASS_COLOURS[n % CLASS_COLOURS.length]!, points: [],
    }]);
    store.set({ activeClass: n });
  };

  return (
    <>
      <label class="chk">
        <input type="checkbox" data-testid="brush" checked={state.brush}
          onChange={(e) => store.set({ brush: (e.target as HTMLInputElement).checked })} />
        <div>Brush training<em>{state.brush
          ? 'Drag across the map to collect a region. Panning is off while this is on.'
          : 'Off: a click collects one pixel, so the class mean depends on exactly where you clicked.'}</em></div>
      </label>
      {state.brush && (
        <div class="row"><label>radius</label>
          <input type="range" data-testid="brush-radius" min="1" max="12" step="1"
            value={state.brushRadius}
            onInput={(e) => store.set({ brushRadius: +(e.target as HTMLInputElement).value })} />
          <span class="num">{state.brushRadius}</span>
        </div>
      )}

      <p class="hint">
        The image is shown underneath while you train. Name a class, click examples of
        it on the map, then add a class for the next material. Every pixel is assigned
        to whichever class it most resembles; anything too far from all of them is left
        unclassified, showing the image through.
      </p>

      {classes.map((c, i) => {
        const mean = classMean(c.points);
        const spread = classSpread(c);
        return (
          <div class={'cls' + (i === activeClass ? ' on' : '')} key={c.id}
            data-testid={`class-${i}`}
            onClick={() => store.set({ activeClass: i })}>
            <div class="cls-head">
              <input type="color" value={'#' + c.colour.map((x) =>
                x.toString(16).padStart(2, '0')).join('')}
                aria-label={`Colour for ${c.name}`}
                onClick={(e) => e.stopPropagation()}
                onInput={(e) => {
                  const hex = (e.target as HTMLInputElement).value;
                  const col: [number, number, number] = [1, 3, 5].map((k) =>
                    parseInt(hex.slice(k, k + 2), 16)) as [number, number, number];
                  set(classes.map((x, k) => (k === i ? { ...x, colour: col } : x)));
                }} />
              <input class="cls-name" value={c.name} aria-label="Class name"
                data-testid={`class-name-${i}`}
                onClick={(e) => e.stopPropagation()}
                onInput={(e) => set(classes.map((x, k) =>
                  (k === i ? { ...x, name: (e.target as HTMLInputElement).value } : x)))} />
              <button class="btn tiny" title="Remove this class"
                onClick={(e) => {
                  e.stopPropagation();
                  set(classes.filter((_, k) => k !== i));
                  store.set({ activeClass: Math.max(0, Math.min(activeClass, classes.length - 2)) });
                }}>×</button>
            </div>
            <div class="cls-meta">
              <span>{c.points.length} point{c.points.length === 1 ? '' : 's'}</span>
              {!mean && <span class="warn">needs a point</span>}
              {/* A class whose own examples disagree is badly drawn, and no threshold
                  will rescue it. */}
              {spread != null && spread > 8 &&
                <span class="warn" title="Training points disagree with each other">
                  spread {spread.toFixed(1)}°</span>}
              {c.points.length > 0 && (
                <button class="btn tiny" title="Remove this class's training points"
                  onClick={(e) => {
                    e.stopPropagation();
                    set(classes.map((x, k) => (k === i ? { ...x, points: [] } : x)));
                  }}>clear</button>
              )}
            </div>
          </div>
        );
      })}

      <button class="btn wide" data-testid="add-class" onClick={addClass}>Add class</button>

      {classes.length > 0 && (
        <div class="row"><label>max angle</label>
          <input type="range" data-testid="max-angle" min="1" max="45" step="0.5"
            value={state.samMaxAngle}
            onInput={(e) => store.set({ samMaxAngle: +(e.target as HTMLInputElement).value })} />
          <span class="num">{state.samMaxAngle.toFixed(1)}°</span>
        </div>
      )}
      {classes.length > 0 && (
        <p class="hint">Pixels further than this from every class stay unclassified,
          which is what keeps bare ground out of a canopy class.</p>
      )}

      {/* Two classes a couple of degrees apart cannot be separated by any classifier. */}
      {worst && worst.deg < 5 && (
        <div class="note bad">
          {classes[worst.a]?.name} and {classes[worst.b]?.name} are only
          {' '}{worst.deg.toFixed(1)}° apart. No classifier will separate them — merge
          them, or add a band that distinguishes them.
        </div>
      )}
      {worst && worst.deg >= 5 && classes.length > 1 && (
        <p class="hint">Closest pair: {classes[worst.a]?.name} and {classes[worst.b]?.name},
          {' '}{worst.deg.toFixed(1)}° apart.</p>
      )}
      <Accuracy classes={classes} maxAngle={state.samMaxAngle} />

      {classes.length > 0 && samBands(profile).length < 2 && (
        <div class="note bad">Fewer than two bands carry a wavelength — assign band
          roles before training.</div>
      )}
    </>
  );
}

/**
 * Hold-out accuracy. Every third training point is withheld, the classifier is rebuilt
 * from the rest, and the withheld points are classified — so the number measures skill
 * rather than memorisation.
 */
function Accuracy({ classes, maxAngle }: { classes: TrainingClass[]; maxAngle: number }) {
  const a = assess(classes, maxAngle);
  if (!a) {
    const short = classes.filter((c) => c.points.length < 3).length;
    if (classes.length >= 2 && short) {
      return <p class="hint">Three or more points per class and accuracy is reported here.</p>;
    }
    return null;
  }
  const pct = (x: number | null) => (x == null ? '—' : `${Math.round(x * 100)}%`);
  const tone = a.overall >= 0.85 ? 'ok' : a.overall >= 0.65 ? 'warn' : 'bad';
  return (
    <div class="acc" data-testid="accuracy">
      <div class="subhead">accuracy</div>
      <div class="kvrow">
        <span>overall, on {a.tested} held-out points</span>
        <b class={tone}>{pct(a.overall)}</b>
      </div>
      <table class="conf">
        <thead>
          <tr><th /><th colSpan={a.names.length + 1}>classified as</th></tr>
          <tr>
            <th />
            {a.names.map((n) => <th key={n} title={n}>{n.slice(0, 6)}</th>)}
            <th title="Matched nothing within the maximum angle">none</th>
          </tr>
        </thead>
        <tbody>
          {a.matrix.map((row, i) => (
            <tr key={i}>
              <th title={a.names[i]}>{a.names[i]!.slice(0, 6)}</th>
              {row.map((v, j) => (
                <td key={j} class={i === j ? 'diag' : v > 0 ? 'off' : ''}>{v || ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div class="acc-per">
        {a.perClass.map((c) => (
          <div key={c.name}>
            <span>{c.name}</span>
            <b>found {pct(c.producer)}</b>
            <b>correct {pct(c.user)}</b>
          </div>
        ))}
      </div>
      <p class="hint">
        Found is how much of a class the classifier picked up; correct is how much of
        what it picked up really belongs. A class high on one and low on the other is
        swallowing its neighbour.
      </p>
    </div>
  );
}

/** Class swatches in place of a colour ramp. */
export function ClassLegend({ classes }: { classes: TrainingClass[] }) {
  const usable = classes.filter((c) => classMean(c.points));
  if (!usable.length) return null;
  return (
    <div class="cls-legend">
      {usable.map((c) => (
        <span key={c.id}><i style={{ background: rgb(c.colour) }} />{c.name}</span>
      ))}
    </div>
  );
}
